import qs from 'qs';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useItemsApi } from './useItemsApi';
import { useAuth } from '../contexts/AuthContext';
import type { BiddingInfo, BiddingInfoData, UpdateBiddingDataRequest, GetItemsFilters, UpcomingBiddingListResponse } from '../types'; // 型をインポート
import toast from 'react-hot-toast';
import type { BiddingInfoResponse } from '../types/BiddingInfoResponse';

//入札時データ一括取得
export const useBiddingInfoGetInfoListForBuyerQuery = (filters: GetItemsFilters, loginId: string,options?: { enabled?: boolean }) => {
  const { biddingInfoGetInfoListForBuyer } = useItemsApi();
  
  return useQuery<UpcomingBiddingListResponse, Error>({ 
    queryKey: ['biddinginfo', 'buyer', filters, loginId ],
    queryFn: async () => {
      
      // 1. フィルタオブジェクトのクリーンアップ (空文字やnullを除外)
      //    これにより URLSearchParams への手動 append が不要になります
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== '')
      );

      // 2. クエリパラメータの生成 (qsライブラリを使用)
      //    arrayFormat: 'repeat' を指定することで、
      //    sortKeys: ['A', 'B'] が "sortKeys=A&sortKeys=B" に変換されます
      //    (ASP.NET Core の [FromQuery] List<string> にバインドされる形式)
      const queryString = qs.stringify(cleanFilters, { arrayFormat: 'repeat' });

      // 3. API呼び出し
      //    ※ biddingItemsGetItemList が URLSearchParams を受け取る仕様なら修正が必要
      //    ここではクエリ文字列、または params オブジェクトを渡す形を想定
      return biddingInfoGetInfoListForBuyer(queryString); 
    },

    // // データを「新鮮(fresh)」と見なす時間を設定します。
    // // この時間内は、画面に戻ってきてもAPIの再実行は行われません。
    // staleTime: 1000 * 30, // 30秒

    // // filtersオブジェクトの値（['', '20250101', null, 123, ...]) を配列として取得し、
    // // その中に1つでも「truthy」な値（空文字、null、undefined、0以外）が存在するかチェック
    // enabled: Object.values(filters).some(Boolean),
    enabled: options?.enabled !== undefined 
      ? options.enabled && !!loginId 
      : !!loginId,
  });
};

//  入札番号を指定して「特定の1件」を取得するフック
export const useBiddingInfoGetInfoForBuyerQuery = (
  biddingDate: string | number, 
  biddingNumber: string,
  loginId: string,
  options?: { enabled?: boolean }
) => {
  const { biddingInfoGetInfoForBuyer } = useItemsApi();

  return useQuery<BiddingInfoResponse, Error>({
    queryKey: ['biddinginfo', 'bidinfo', biddingDate, biddingNumber, loginId],
    queryFn: () => biddingInfoGetInfoForBuyer(biddingDate, biddingNumber),
    enabled: options?.enabled !== undefined 
      ? options.enabled && !!loginId 
      : !!loginId,
  });
};

export const useUpsertBidMutation = () => {
    const queryClient = useQueryClient();
    const { upsertBid } = useItemsApi(); // API呼び出し関数を取得

    return useMutation<
        BiddingInfoData, // onSuccessで受け取るデータの型 (バックエンドの戻り値に合わせる)
        Error,           // onErrorで受け取るエラーの型
        { itemId: string; branchNumber: number, requests: UpdateBiddingDataRequest } // mutate関数に渡す変数の型
      >({
      // mutationFn: mutate関数に渡された変数を使ってAPI呼び出しを行う関数
      mutationFn: (variables) => upsertBid(variables.itemId, variables.branchNumber, variables.requests),

      onSuccess: () => {
        toast.success('入札データを更新しました。');
        // 関連する一覧データのキャッシュを無効化し、再取得を促す
        queryClient.invalidateQueries({ queryKey: ['biddinginfo', 'buyer'] });

        // お気に入り情報も再取得する
        queryClient.invalidateQueries({ queryKey: ["favorites"] });
      },

      onError: (error) => {
        toast.error(error instanceof Error ? error.message : "入札データの更新に失敗しました。");
      },
    });
  };

export const useUpdateBidDisabledMutation = () => {
  const queryClient = useQueryClient();
  const { updateBidDisabled } = useItemsApi();

  return useMutation<
    BiddingInfoData,
    any, 
    { itemId: string; branchNumber: number; buyerCode: number; isDisabled: boolean }
  >({
    mutationFn: (v) => updateBidDisabled(v.itemId, v.branchNumber, v.buyerCode, v.isDisabled),

    onSuccess: () => {
      toast.success('無効状態を更新しました。');
      queryClient.invalidateQueries({ queryKey: ['biddingResultConfirm'] });
    },

    onError: (error: any) => {
      // ━━━ ここでバックエンドを確実に拾う ━━━
      // 1. レスポンスデータを取得（?. を使うことで response が無くてもクラッシュしない）
      const data = error.response?.data;

      // 2. メッセージを抽出（ASP.NETの返し方によって場所が変わるため、優先順位をつけて探す）
      const displayMessage = 
        (typeof data === 'string' ? data : null) || // プレーンテキストで返ってきた場合
        data?.message ||                            // { "message": "..." } の場合
        data?.detail ||                             // ASP.NET標準のエラー形式の場合
        "更新に失敗しました。";                       // どれもダメだった時の最終手段

      // 3. トーストに表示
      toast.error(displayMessage, {
        duration: 3000, // 長いメッセージを読み切れるように少し長めに表示
      });

    },
  });
};

  // 市場や売手など、買手以外でも一覧を取得するためのHook
 export const useBiddingItemsGetItemListQuery = (filters: any, loginId: string) => {
  const { currentUser } = useAuth(); // ★自分の情報を取得
  
  // Buyer用APIと、汎用(Admin/Seller)APIの両方を用意
  const { 
    biddingItemsGetItemList,        // 汎用
    biddingInfoGetInfoListForBuyer  // Buyer用
  } = useItemsApi();

  return useQuery<BiddingInfo[], Error>({
    // キーに role を含めて、権限が変わったら再取得するようにする
    queryKey: ['listingSchedule', currentUser?.role, filters, loginId],
    
    queryFn: async () => {
      // フィルタ整形
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== '')
      );
      const queryString = qs.stringify(cleanFilters, { arrayFormat: 'repeat' });

      // ★ここで分岐！
      // ログインユーザーが「Buyer (買い手)」かどうか判定
      // ※ roleの文字列("Buyer")はプロジェクトの定数に合わせてください
      if (currentUser?.role === 'Buyer') {
          // ▼ 変更点：Buyer用APIは「オブジェクト」を返すので、
          // その中から `.items` (配列部分) だけを取り出して返すようにします。
          const response = await biddingInfoGetInfoListForBuyer(queryString);
          return response.items; // ◀ これで Promise<BiddingInfo[]> になります！
      } else {
          // B. それ以外(管理者・売手)の場合
          const response = await biddingItemsGetItemList(queryString);
          return response as unknown as BiddingInfo[];
      }
    },
    // loginIdがあれば実行許可
    enabled: !!loginId,
  });
};